import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const CATEGORIES = [
  { slug: "all", label: "All" },
  { slug: "rings", label: "Rings" },
  { slug: "necklaces", label: "Necklaces" },
  { slug: "earrings", label: "Earrings" },
  { slug: "bracelets", label: "Bracelets" },
  { slug: "pendants", label: "Pendants" },
];

const SORTS = [
  { value: "", label: "Curated" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price · Low → High" },
  { value: "price_desc", label: "Price · High → Low" },
  { value: "name_asc", label: "Name · A → Z" },
];

export default function Collection() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const active = category || "all";
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ materials: [], min_price: 0, max_price: 0 });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter state pulled from URL
  const q = searchParams.get("q") || "";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";
  const material = searchParams.get("material") || "";
  const inStock = searchParams.get("in_stock") === "1";
  const sort = searchParams.get("sort") || "";

  const updateParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v === null || v === false) next.delete(k);
    else next.set(k, v === true ? "1" : v);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => setSearchParams({});

  useEffect(() => {
    api.get("/meta/filters").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (active !== "all") params.category = active;
    if (q) params.q = q;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    if (material) params.material = material;
    if (inStock) params.in_stock = true;
    if (sort) params.sort = sort;
    api.get("/products", { params }).then((r) => setProducts(r.data));
  }, [active, q, minPrice, maxPrice, material, inStock, sort]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (q) n++;
    if (minPrice) n++;
    if (maxPrice) n++;
    if (material) n++;
    if (inStock) n++;
    return n;
  }, [q, minPrice, maxPrice, material, inStock]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-24">
      <div className="overline rise rise-1">The atelier</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-4 rise rise-2">
        {active === "all"
          ? "Every piece"
          : CATEGORIES.find((c) => c.slug === active)?.label || "Collection"}
      </h1>

      {/* Search bar */}
      <div className="mt-8 max-w-xl relative rise rise-3">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5a56]" />
        <input
          value={q}
          onChange={(e) => updateParam("q", e.target.value)}
          placeholder="Search by name, material or style…"
          className="w-full pl-10 pr-10 py-3 border border-[#ebe5df] bg-white focus:outline-none focus:border-[#c58b76] text-sm"
          data-testid="collection-search"
        />
        {q && (
          <button
            onClick={() => updateParam("q", "")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5c5a56] hover:text-[#1a1918]"
            data-testid="search-clear"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mt-8 border-b border-[#ebe5df] pb-4 rise rise-4">
        {CATEGORIES.map((c) => (
          <button
            key={c.slug}
            onClick={() =>
              navigate(c.slug === "all" ? "/collection" : `/collection/${c.slug}`)
            }
            className={`px-4 py-1.5 text-xs tracking-[0.18em] uppercase border ${
              active === c.slug
                ? "bg-[#1a1918] text-white border-[#1a1918]"
                : "border-transparent text-[#5c5a56] hover:text-[#1a1918]"
            }`}
            data-testid={`filter-${c.slug}`}
          >
            {c.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#1a1918] hover:text-[#c58b76]"
            data-testid="toggle-filters"
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-[#c58b76] text-white text-[10px] px-1.5 py-0.5 ml-1">
                {activeFilterCount}
              </span>
            )}
          </button>
          <select
            value={sort}
            onChange={(e) => updateParam("sort", e.target.value)}
            className="text-xs tracking-[0.16em] uppercase border border-[#ebe5df] bg-white px-3 py-1.5 focus:outline-none focus:border-[#c58b76]"
            data-testid="sort-select"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters panel */}
      {filtersOpen && (
        <div className="mt-6 border border-[#ebe5df] bg-[#fdfbf7] p-6 grid md:grid-cols-4 gap-6" data-testid="filters-panel">
          <div>
            <label className="overline block mb-2">Min price (₹)</label>
            <input
              type="number"
              value={minPrice}
              onChange={(e) => updateParam("min_price", e.target.value)}
              placeholder={`${meta.min_price || 0}`}
              className="w-full px-3 py-2 border border-[#ebe5df] bg-white focus:outline-none focus:border-[#c58b76] text-sm"
              data-testid="filter-min-price"
            />
          </div>
          <div>
            <label className="overline block mb-2">Max price (₹)</label>
            <input
              type="number"
              value={maxPrice}
              onChange={(e) => updateParam("max_price", e.target.value)}
              placeholder={`${meta.max_price || 0}`}
              className="w-full px-3 py-2 border border-[#ebe5df] bg-white focus:outline-none focus:border-[#c58b76] text-sm"
              data-testid="filter-max-price"
            />
          </div>
          <div>
            <label className="overline block mb-2">Material</label>
            <select
              value={material}
              onChange={(e) => updateParam("material", e.target.value)}
              className="w-full px-3 py-2 border border-[#ebe5df] bg-white focus:outline-none focus:border-[#c58b76] text-sm"
              data-testid="filter-material"
            >
              <option value="">Any material</option>
              {meta.materials.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col justify-between">
            <label className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={inStock}
                onChange={(e) => updateParam("in_stock", e.target.checked)}
                data-testid="filter-in-stock"
              />
              <span className="text-sm text-[#1a1918]">In stock only</span>
            </label>
            <button
              onClick={clearFilters}
              className="mt-4 text-xs tracking-[0.18em] uppercase text-[#8b3a36] hover:text-[#1a1918] self-start"
              data-testid="clear-filters"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 text-sm text-[#5c5a56]" data-testid="results-count">
        {products.length} {products.length === 1 ? "piece" : "pieces"} found
        {q && ` for "${q}"`}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mt-8">
        {products.length === 0 ? (
          <div className="col-span-full text-center text-[#5c5a56] py-24">
            <div className="font-serif text-2xl mb-2">No pieces match those filters.</div>
            <button
              onClick={clearFilters}
              className="text-xs uppercase tracking-[0.18em] text-[#c58b76] mt-3 hover:text-[#1a1918]"
              data-testid="empty-clear"
            >
              Clear filters
            </button>
          </div>
        ) : (
          products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)
        )}
      </div>
    </main>
  );
}
